import type { HealthCheckFinding } from '../models/healthCheck.types'
import type { HealthCheckStore } from '../stores/healthCheckStore'

import type { HealthCheckNotifier } from './healthCheckFlow.types'

import type { MetaCheckpointVerifyOutcome } from '@/features/integrations'

export interface VerifyCheckpointFlowDeps {
  store: Pick<
    HealthCheckStore,
    'actionPending' | 'setActionPending' | 'setCheckpointNotice' | 'selectedId' | 'clearSelection'
  >
  /** Module 12's verify: it re-reads `account_detail` itself when the block is gone. */
  verify: (path: string) => Promise<MetaCheckpointVerifyOutcome>
  translate: (key: string) => string
  notifier: Pick<HealthCheckNotifier, 'success'>
  reload: () => Promise<void>
}

/**
 * "I've completed it" on the Meta checkpoint card (ORP-26183). A block Meta still shows is answered
 * inline on the card, not with a toast, because the card already says what to do next; a cleared
 * one reloads the findings, which no longer carry the card.
 */
export class VerifyCheckpointFlow {
  constructor(private readonly deps: VerifyCheckpointFlowDeps) {}

  async execute(finding: HealthCheckFinding): Promise<void> {
    const action = finding.action
    if (action?.kind !== 'apiPost' || this.deps.store.actionPending[finding.id]) return

    this.deps.store.setActionPending(finding.id, true)
    try {
      const outcome = await this.deps.verify(action.target)
      if (outcome.status === 'canceled') return
      if (outcome.status === 'blocked') {
        this.deps.store.setCheckpointNotice(
          finding.id,
          this.deps.translate('healthChecks.checkpoint.stillBlocked'),
        )
        return
      }
      if (outcome.status === 'failed') {
        this.deps.store.setCheckpointNotice(
          finding.id,
          outcome.detail ?? this.deps.translate('healthChecks.checkpoint.verifyFailed'),
        )
        return
      }
      this.deps.store.setCheckpointNotice(finding.id, null)
      if (this.deps.store.selectedId === finding.id) this.deps.store.clearSelection()
      if (outcome.status === 'cleared')
        this.deps.notifier.success('healthChecks.checkpoint.cleared')
      await this.deps.reload()
    } finally {
      this.deps.store.setActionPending(finding.id, false)
    }
  }
}
